// Normalización de teléfonos a E.164. Por defecto se asume Norteamérica (+1).

/**
 * "(305) 555-0199" -> "+13055550199"; "+52 55 1234 5678" -> "+525512345678".
 * Devuelve null si no parece un número válido.
 */
export function normalizePhone(raw: string, defaultCountryCode = '1'): string | null {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const hasPlus = trimmed.startsWith('+');
  const digits = trimmed.replace(/\D/g, '');
  if (hasPlus) {
    return digits.length >= 8 && digits.length <= 15 ? `+${digits}` : null;
  }
  if (defaultCountryCode === '1') {
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
    return null;
  }
  return digits.length >= 6 && digits.length <= 15 - defaultCountryCode.length
    ? `+${defaultCountryCode}${digits}`
    : null;
}

/** Formato legible para números de +1: "+13055550199" -> "(305) 555-0199". */
export function formatPhone(e164: string): string {
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164);
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : e164;
}

/** Solo dígitos, para búsquedas parciales (por ejemplo, últimos 4 dígitos en el kiosko). */
export function phoneDigits(raw: string): string {
  return (raw || '').replace(/\D/g, '');
}

/**
 * Número nacional válido por país (sin el 0 inicial que se marca dentro del país). Sirve para
 * distinguir un celular de Venezuela («0414-5331845») de uno de EE. UU. sin adivinar.
 */
const NATIONAL_NUMBER: Record<string, RegExp> = {
  '1': /^[2-9]\d{9}$/,
  '58': /^[24589]\d{9}$/, // Venezuela: 0414…, 0424…, 0251…
  '34': /^[6789]\d{8}$/, // España
  '52': /^\d{10}$/, // México
  '57': /^[1-8]\d{9}$/, // Colombia
  '51': /^\d{8,9}$/, // Perú
  '56': /^\d{9}$/, // Chile
  '54': /^9?\d{10}$/, // Argentina
  '506': /^\d{8}$/, '507': /^\d{7,8}$/, '502': /^\d{8}$/, '503': /^\d{8}$/, '504': /^\d{8}$/, '505': /^\d{8}$/,
};

/** Código de país para marcar según la zona horaria del campus (las que ofrece la plataforma). */
const COUNTRY_BY_TIMEZONE: Record<string, string> = {
  'America/Caracas': '58', 'Europe/Madrid': '34', 'America/Mexico_City': '52', 'America/Bogota': '57',
  'America/Lima': '51', 'America/Santiago': '56', 'America/Argentina/Buenos_Aires': '54', 'America/Guatemala': '502',
  'America/El_Salvador': '503', 'America/Tegucigalpa': '504', 'America/Managua': '505', 'America/Costa_Rica': '506',
  'America/Panama': '507',
};

export function countryCodeForTimezone(timezone: string | null | undefined): string {
  return (timezone && COUNTRY_BY_TIMEZONE[timezone]) || '1';
}

/**
 * Teléfono a E.164 probando los países en orden (por ejemplo, primero el del campus de la
 * persona). Acepta «+58 414…», «0058 414…» y el formato local de cada país («0414-5331845»).
 */
export function normalizePhoneForCountries(raw: string, countryCodes: string[]): string | null {
  const trimmed = (raw || '').trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('+')) return normalizePhone(trimmed);
  const digits = trimmed.replace(/\D/g, '');
  if (digits.startsWith('00')) return normalizePhone(`+${digits.slice(2)}`);
  for (const cc of [...new Set(countryCodes)]) {
    if (cc === '1') {
      const us = normalizePhone(digits, '1');
      if (us && NATIONAL_NUMBER['1']!.test(us.slice(2))) return us;
      continue;
    }
    const national = digits.replace(/^0/, '');
    const rule = NATIONAL_NUMBER[cc];
    if (rule ? rule.test(national) : national.length >= 6 && national.length <= 15 - cc.length) return `+${cc}${national}`;
  }
  return null;
}
